import { useState, useEffect } from 'react';
import { X, DollarSign, TrendingUp, Sliders, Loader2, Save } from 'lucide-react';

/* ─── Shared slider ─── */
function NativeSlider({ value, min, max, step, onChange }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative flex items-center h-10">
      <div className="absolute left-0 right-0 h-[5px] rounded-full"
        style={{ background: 'var(--bg-tertiary, #e5e7eb)' }} />
      <div className="absolute left-0 h-[5px] rounded-full"
        style={{ width: `${pct}%`, background: 'var(--accent-primary, #3b82f6)', transition: 'width .05s' }} />
      <input type="range" min={min} max={max} step={step} value={value} onChange={onChange}
        className="sym-thumb w-full h-10 absolute left-0 cursor-pointer bg-transparent appearance-none" />
    </div>
  );
}

function SliderRow({ label, hint, value, displayValue, min, max, step, onChange }) {
  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-1">
        <label className="text-[15px] font-medium" style={{ color: 'var(--text-primary, #111)' }}>{label}</label>
        <span className="text-sm font-mono font-semibold px-2 py-0.5 rounded-full"
          style={{
            background: 'color-mix(in srgb, var(--accent-primary,#3b82f6) 12%, transparent)',
            color: 'var(--accent-primary, #3b82f6)',
          }}>
          {displayValue ?? value}
        </span>
      </div>
      <NativeSlider value={value} min={min} max={max} step={step} onChange={onChange} />
      {hint && <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>{hint}</p>}
    </div>
  );
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
function DayPicker({ value, onChange }) {
  return (
    <div className="py-3">
      <p className="text-[15px] font-medium mb-2" style={{ color: 'var(--text-primary, #111)' }}>Triple Swap Day</p>
      <div className="grid grid-cols-7 gap-1">
        {DAYS.map((d, i) => {
          const n = i + 1; const active = value === n;
          return (
            <button key={d} type="button" onClick={() => onChange(n)}
              className="rounded-lg py-2 text-xs font-semibold transition-all active:scale-95"
              style={{
                background: active ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-secondary, #f3f4f6)',
                color: active ? '#fff' : 'var(--text-secondary, #6b7280)',
              }}>
              {d}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>Day when triple swap is charged</p>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl overflow-hidden mb-4"
      style={{ background: 'var(--bg-secondary, #f9fafb)', border: '1px solid var(--border-color, #e5e7eb)' }}>
      <div className="flex items-center gap-2 px-4 py-3"
        style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
        <Icon className="w-4 h-4" style={{ color: 'var(--accent-primary, #3b82f6)' }} />
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary, #6b7280)' }}>
          {title}
        </span>
      </div>
      <div className="px-4 divide-y" style={{ borderColor: 'var(--border-color, #e5e7eb)' }}>
        {children}
      </div>
    </div>
  );
}

export default function SymbolSettingsModal({ symbol, onClose, onSave, fetchSymbolSettings }) {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    spread_markup: 0.0, commission_per_lot: 0.0, swap_markup: 0.0,
    triple_swap_day: 3, max_leverage: 500, max_lot_size: 100.0,
    stop_level: 10, freeze_level: 5,
  });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const s = (await fetchSymbolSettings(symbol)).data;
      setFormData({
        spread_markup:      s.spread_markup      ?? 0.0,
        commission_per_lot: s.commission_per_lot ?? 0.0,
        swap_markup:        s.swap_markup        ?? 0.0,
        triple_swap_day:    s.triple_swap_day    ?? 3,
        max_leverage:       s.max_leverage       ?? 500,
        max_lot_size:       s.max_lot_size       ?? 100.0,
        stop_level:         s.stop_level         ?? 10,
        freeze_level:       s.freeze_level       ?? 5,
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const set = (f, v) => setFormData(p => ({ ...p, [f]: v }));
  const handleSubmit = (e) => { e?.preventDefault(); onSave(formData); };

  const formBody = loading ? (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary, #3b82f6)' }} />
      <p className="text-sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>Loading settings…</p>
    </div>
  ) : (
    <>
      <Section icon={DollarSign} title="Broker Pricing">
        <SliderRow label="Spread Markup" hint="Pips added to raw spread from liquidity provider"
          value={formData.spread_markup} displayValue={`${formData.spread_markup} pips`}
          min={0} max={20000} step={0.1} onChange={e => set('spread_markup', parseFloat(e.target.value))} />
        <SliderRow label="Commission Per Lot" hint="USD charged per standard lot traded"
          value={formData.commission_per_lot} displayValue={`$${formData.commission_per_lot}`}
          min={0} max={50} step={0.5} onChange={e => set('commission_per_lot', parseFloat(e.target.value))} />
        <SliderRow label="Swap Markup" hint="Points added to overnight swap rates"
          value={formData.swap_markup} displayValue={`${formData.swap_markup} pts`}
          min={0} max={5} step={0.1} onChange={e => set('swap_markup', parseFloat(e.target.value))} />
        <DayPicker value={formData.triple_swap_day} onChange={v => set('triple_swap_day', v)} />
      </Section>
      <Section icon={TrendingUp} title="Risk Limits">
        <SliderRow label="Max Leverage" hint="Maximum leverage allowed for this symbol"
          value={formData.max_leverage} displayValue={`1:${formData.max_leverage}`}
          min={1} max={2000} step={1} onChange={e => set('max_leverage', parseInt(e.target.value))} />
        <SliderRow label="Max Lot Size" hint="Maximum position size per trade"
          value={formData.max_lot_size} displayValue={`${formData.max_lot_size} lots`}
          min={0.01} max={500} step={0.01} onChange={e => set('max_lot_size', parseFloat(e.target.value))} />
      </Section>
      <Section icon={Sliders} title="Execution Rules">
        <SliderRow label="Stop Level" hint="Minimum distance for SL/TP from current market price"
          value={formData.stop_level} displayValue={`${formData.stop_level} pips`}
          min={0} max={100} step={1} onChange={e => set('stop_level', parseInt(e.target.value))} />
        <SliderRow label="Freeze Level" hint="Distance where order modifications are frozen"
          value={formData.freeze_level} displayValue={`${formData.freeze_level} pips`}
          min={0} max={50} step={1} onChange={e => set('freeze_level', parseInt(e.target.value))} />
      </Section>
    </>
  );

  return (
    <>
      <style>{`
        /* ── Shared thumb style ── */
        .sym-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 26px; height: 26px; border-radius: 50%;
          background: var(--accent-primary, #3b82f6);
          box-shadow: 0 1px 4px rgba(0,0,0,.25), 0 0 0 3px color-mix(in srgb, var(--accent-primary,#3b82f6) 18%, transparent);
          border: 2.5px solid #fff; cursor: pointer; transition: transform .1s;
        }
        .sym-thumb:active::-webkit-slider-thumb { transform: scale(1.15); }
        .sym-thumb::-moz-range-thumb {
          width: 26px; height: 26px; border-radius: 50%;
          background: var(--accent-primary, #3b82f6);
          border: none; cursor: pointer;
        }

        /* ── Mobile: bottom sheet ── */
        @media (max-width: 767px) {
          .sym-backdrop { align-items: flex-end; }
          .sym-dialog {
            width: 100%; max-width: 100%;
            border-radius: 20px 20px 0 0;
            max-height: 92dvh;
            animation: symSlideUp .28s cubic-bezier(.32,.72,0,1);
          }
          .sym-handle { display: flex; }
          .sym-footer {
            padding: 12px 16px;
            padding-bottom: max(16px, env(safe-area-inset-bottom));
          }
          .sym-footer button {
            padding-top: 14px; padding-bottom: 14px;
            border-radius: 16px; font-size: 16px; font-weight: 600;
          }
        }

        /* ── Desktop: original centered dialog ── */
        @media (min-width: 768px) {
          .sym-backdrop { align-items: center; justify-content: center; padding: 16px; }
          .sym-dialog {
            width: 100%; max-width: 768px;
            border-radius: 16px; max-height: 90vh;
            border: 1px solid var(--border-color);
          }
          .sym-handle { display: none; }
          .sym-footer { padding: 24px; }
          .sym-footer button {
            padding: 12px 24px; border-radius: 8px;
            font-size: 14px; font-weight: 500;
          }
        }

        @keyframes symSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      <div className="sym-backdrop fixed inset-0 z-50 flex"
        style={{ background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }}
        onClick={e => e.target === e.currentTarget && onClose()}>

        <div className="sym-dialog flex flex-col overflow-hidden"
          style={{ background: 'var(--bg-primary, #fff)' }}>

          {/* Mobile drag handle */}
          <div className="sym-handle justify-center pt-3 pb-1 flex-shrink-0">
            <div className="w-10 h-[5px] rounded-full" style={{ background: 'var(--border-color, #d1d5db)' }} />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
            <div>
              <h2 className="text-xl font-bold">{symbol} Settings</h2>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Configure broker-level pricing and risk (Layer 3)
              </p>
            </div>
            <button onClick={onClose}
              className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 py-4" style={{ WebkitOverflowScrolling: 'touch' }}>
            {formBody}
            <div className="h-2" />
          </div>

          {/* Footer */}
          <div className="sym-footer flex gap-3 flex-shrink-0"
            style={{ borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} disabled={loading}
              className="flex-1 bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-primary-hover)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              <Save className="w-4 h-4" />
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </>
  );
}